import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ConfirmationModal from '@components/atoms/modals/confirmation-modal.tsx';
import {
  getLiveSession,
  getLiveStartRequest,
  stopLiveSession
} from '@utils/api/realtime/live-transcript.ts';
import { forgetSessionSource } from '@utils/editors/live-session-source.ts';
import { useRealtimeContext } from '@utils/providers/realtime-context.tsx';
import { useRealtimeEditorContext } from '@utils/providers/realtime-editor-context.tsx';
import { LiveSessionStatus } from '@utils/types/editors/live-transcript.ts';

type LiveState = {
  sessionId: string;
  scope: string;
  owner: LiveSessionStatus | null;
  saving: boolean;
  discard: () => boolean;
};
type Options = {
  sessionId: string;
  identityScope: string;
  live: LiveState;
  onFresh: () => void;
};
const terminal = (status: LiveSessionStatus) =>
  status.state === 'stopped' || status.state === 'failed';

export const useRealtimeFreshSession = ({
  sessionId,
  identityScope,
  live,
  onFresh
}: Options) => {
  const { t } = useTranslation();
  const realtime = useRealtimeContext();
  const editor = useRealtimeEditorContext();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const requestId = params.get('live_start') || '';
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const scope = useRef(0);
  const busyRef = useRef(false);
  useEffect(() => {
    scope.current += 1;
    busyRef.current = false;
    setBusy(false);
    setOpen(false);
  }, [identityScope, requestId, sessionId]);
  useEffect(
    () => () => {
      scope.current += 1;
    },
    []
  );

  const close = useCallback(() => {
    if (!busy) {
      setOpen(false);
      setError(null);
    }
  }, [busy]);
  const start = useCallback(() => {
    if (busyRef.current) return;
    setError(null);
    setOpen(true);
  }, [busy]);
  const confirm = useCallback(async () => {
    const reportedCurrent =
      live.sessionId === sessionId &&
      live.scope === `${identityScope}:${sessionId}`;
    if (busyRef.current || (reportedCurrent && live.saving)) {
      if (reportedCurrent && live.saving)
        setError(t('live-fresh-save-pending'));
      return;
    }
    const activeScope = scope.current;
    if (realtime.startPending()) {
      setError(t('live-fresh-start-pending'));
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const stillCurrent = () => scope.current === activeScope;
      let currentId = sessionId;
      if (!currentId && requestId) {
        let request: LiveSessionStatus;
        try {
          request = await getLiveStartRequest(requestId);
        } catch {
          throw new Error('live-fresh-start-unknown');
        }
        if (!stillCurrent()) return;
        if (request.session_id) currentId = request.session_id;
        else if (request.state !== 'failed')
          throw new Error('live-fresh-start-pending');
      }
      const currentLive =
        !!currentId &&
        live.sessionId === currentId &&
        live.scope === `${identityScope}:${currentId}`;
      let owner: LiveSessionStatus | null =
        currentLive &&
        live.owner?.session_id === currentId &&
        terminal(live.owner)
          ? live.owner
          : null;
      if (currentId) {
        owner ||= await getLiveSession(currentId);
        if (!stillCurrent()) return;
        if (owner.can_stop && !terminal(owner)) {
          owner = await stopLiveSession(currentId);
          if (!stillCurrent()) return;
          for (
            let attempt = 0;
            !terminal(owner) && attempt < 75;
            attempt += 1
          ) {
            await new Promise(resolve => setTimeout(resolve, 400));
            if (!stillCurrent()) return;
            owner = await getLiveSession(currentId);
            if (!stillCurrent()) return;
          }
          if (!terminal(owner)) throw new Error('live-fresh-stop-timeout');
        }
      }
      if (!stillCurrent()) return;
      if (currentId && !currentLive)
        throw new Error('live-fresh-current-loading');
      // A start can begin while we wait for an owner to stop. Never clear its
      // recovery state or the current draft until it is known not to be pending.
      if (realtime.startPending()) throw new Error('live-fresh-start-pending');
      // This is deliberately after the stop is known terminal. It removes only
      // this identity's local recovery, never persisted transcript history.
      if (currentLive && live.saving)
        throw new Error('live-fresh-save-pending');
      if (currentLive || !live.sessionId) {
        try {
          if (!live.discard()) throw new Error('live-fresh-clear-failed');
        } catch {
          throw new Error('live-fresh-clear-failed');
        }
      }
      if (currentId) forgetSessionSource(currentId);
      if (requestId) forgetSessionSource(requestId);
      if (owner?.request_id) forgetSessionSource(owner.request_id);
      if (!realtime.reset()) throw new Error('live-fresh-start-pending');
      editor.reset();
      onFresh();
      setOpen(false);
      navigate('/realtime', { replace: true });
    } catch (failure) {
      if (scope.current === activeScope) {
        const key =
          failure instanceof Error &&
          [
            'live-fresh-start-pending',
            'live-fresh-start-unknown',
            'live-fresh-stop-timeout',
            'live-fresh-save-pending',
            'live-fresh-clear-failed',
            'live-fresh-current-loading'
          ].includes(failure.message)
            ? failure.message
            : 'live-fresh-stop-failed';
        setError(t(key));
      }
    } finally {
      if (scope.current === activeScope) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }, [
    editor,
    identityScope,
    live,
    navigate,
    onFresh,
    realtime,
    requestId,
    sessionId,
    t
  ]);
  const readonlyActive =
    live.sessionId === sessionId &&
    live.scope === `${identityScope}:${sessionId}` &&
    live.owner?.session_id === sessionId &&
    !live.owner.can_stop &&
    !terminal(live.owner);
  const copy = readonlyActive
    ? t('live-fresh-readonly-copy')
    : t('live-fresh-copy');
  return {
    open: start,
    busy,
    dialog: (
      <ConfirmationModal
        isOpen={open}
        onClose={busy ? () => {} : close}
        onConfirm={confirm}
        title={t('live-fresh-title')}
        caption={copy}
        confirmLabel={t('live-fresh-confirm')}
        cancelLabel={t('cancel')}
        confirmDisabled={busy}
      >
        {error && <p role="alert">{error}</p>}
      </ConfirmationModal>
    )
  };
};
